import { useCallback, useEffect, useRef, useState } from 'react';

// Small enough that a couple pixels of rubber-banding/inertial overscroll doesn't flip this,
// but not so large that a real scroll-down feels like it takes forever to react.
const SCROLL_DOWN_THRESHOLD_PX = 24;

/**
 * Starts expanded (`isCollapsed: false`) - collapses only once the page has actually scrolled
 * down past `SCROLL_DOWN_THRESHOLD_PX`, and re-expands the moment the page scrolls back to
 * the very top, whenever `resetKey` changes (e.g. the current route), or when `expand()` is
 * called (e.g. tapping the collapsed nav) - until the next scroll down collapses it again.
 *
 * @param enabled `isCollapsed` is `false` unconditionally while this is `false`, and doesn't
 * attach any scroll listener - pass the same condition that gates whether the collapsible nav
 * even renders at all (e.g. mobile viewport + a route that shows it).
 * @param resetKey Resets back to expanded whenever this changes.
 */
export function useScrollCollapse(enabled: boolean, resetKey: unknown): { isCollapsed: boolean; expand: () => void } {
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const lastResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      setHasScrolledDown(false);
    }
  }, [resetKey]);

  useEffect(() => {
    if (!enabled) {
      setHasScrolledDown(false);
      return undefined;
    }

    // This app's own shell (`MainNavLayout`'s `#main-layout`) is `overflow-hidden` all the
    // way down to where `{children}` renders - `window`/`document.body` never actually
    // scrolls here, individual pages define their own scrollable region instead. `scroll`
    // events don't bubble, so a listener on `window` never fires for that inner scroll at
    // all - only a *capture*-phase listener on `document` sees scroll events from any
    // descendant, regardless of which specific element scrolled, without needing to know
    // which one that is ahead of time. `event.target` is that actual scrolled element.
    const handleScroll = (event: Event) => {
      const target = event.target as Document | Element;
      const scrollTop = target instanceof Document ? target.scrollingElement?.scrollTop ?? 0 : target.scrollTop;
      if (scrollTop <= 0) {
        setHasScrolledDown(false);
      } else if (scrollTop > SCROLL_DOWN_THRESHOLD_PX) {
        setHasScrolledDown(true);
      }
    };

    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [enabled]);

  const expand = useCallback(() => setHasScrolledDown(false), []);

  return { isCollapsed: enabled && hasScrolledDown, expand };
}
